/**
 * Which Sales page tab is showing, and which issue's drawer is open. The route keeps both in
 * the address (?tab=issues&issue=<id>) so the comment email can link straight to an issue;
 * without a route (a unit test) the view keeps them itself.
 */

import { useState } from 'react';

export interface SalesSearch {
  tab?: string;
  issue?: string;
}

export interface SalesNav extends SalesSearch {
  navigate: (next: SalesSearch) => void;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseSalesSearch(search: Record<string, unknown>): SalesSearch {
  const out: SalesSearch = {};
  if (typeof search.tab === 'string' && /^[a-z]{1,20}$/.test(search.tab)) out.tab = search.tab;
  if (typeof search.issue === 'string' && UUID.test(search.issue)) out.issue = search.issue;
  return out;
}

/** The tab (one of `tabs`, else the first) and the open issue, with setters. */
export function useSalesTabs<T extends string>(nav: SalesNav | undefined, tabs: readonly T[]) {
  const [local, setLocal] = useState<SalesSearch>({});
  const state: SalesNav = nav ?? { ...local, navigate: setLocal };
  const tab = (tabs as readonly string[]).includes(state.tab ?? '') ? (state.tab as T) : tabs[0];
  return {
    tab,
    issueId: state.issue ?? null,
    setTab: (next: string) => state.navigate({ tab: next }),
    setIssue: (issueId: string | null) => state.navigate({ tab: 'issues', ...(issueId ? { issue: issueId } : {}) }),
  };
}
